/**
 * Digital Subtraction Angiography (DSA) — pipeline de píxeles (CPU).
 *
 * Convención clínica (Invert LUT OFF por defecto):
 *   raw = I_frame − I_mask
 *   si |raw| < noiseFloor → 0 (aplana fondo)
 *   v = raw · gain + offset
 *   out = invert ? (min+max) − clamp(v) : clamp(v)
 *
 * OFF (invert=false): contraste resta → vasos oscuros (cineangiografía).
 * ON  (invert=true):  LUT invertida → vasos claros.
 *
 * Nota perf: Cornerstone 2D no expone fragment shader por frame DICOM;
 * mantenemos máscara + outBuffer en RAM/reuso (sin alloc por frame). WebGL
 * dedicado sería un viewport paralelo, no este path.
 */

/**
 * Quita el sufijo ?frame=N de un imageId cornerstone.
 * @param {string|null|undefined} imageId
 * @returns {string|null}
 */
export function dsaBaseImageId(imageId) {
  if (imageId == null || imageId === '') return null
  return String(imageId).replace(/\?frame=\d+$/i, '')
}

/**
 * Offset/bias por defecto: mitad del rango dinámico.
 * @param {{ minPixelValue?: number, maxPixelValue?: number }|null|undefined} image
 * @returns {number}
 */
export function dsaDefaultBias(image) {
  const min =
    image && image.minPixelValue != null && Number.isFinite(Number(image.minPixelValue))
      ? Number(image.minPixelValue)
      : 0
  const max =
    image && image.maxPixelValue != null && Number.isFinite(Number(image.maxPixelValue))
      ? Number(image.maxPixelValue)
      : 4095
  if (max <= min) return 0
  return Math.round((min + max) / 2)
}

/**
 * Muestreo bilineal de máscara (shift sub-píxel). Fuera de borde → 0.
 * @param {TypedArray|ArrayLike<number>} mask
 * @param {number} cols
 * @param {number} rows
 * @param {number} fx
 * @param {number} fy
 * @returns {number}
 */
export function sampleMaskBilinear(mask, cols, rows, fx, fy) {
  if (fx < 0 || fy < 0 || fx > cols - 1 || fy > rows - 1) {
    // Fuera: clamp suave a borde si está a <1 px; si no, 0
    if (fx < -0.5 || fy < -0.5 || fx > cols - 0.5 || fy > rows - 0.5) {
      return 0
    }
    fx = Math.max(0, Math.min(cols - 1, fx))
    fy = Math.max(0, Math.min(rows - 1, fy))
  }
  const x0 = Math.floor(fx)
  const y0 = Math.floor(fy)
  const x1 = Math.min(cols - 1, x0 + 1)
  const y1 = Math.min(rows - 1, y0 + 1)
  const tx = fx - x0
  const ty = fy - y0
  const v00 = mask[y0 * cols + x0]
  const v10 = mask[y0 * cols + x1]
  const v01 = mask[y1 * cols + x0]
  const v11 = mask[y1 * cols + x1]
  const a = v00 + (v10 - v00) * tx
  const b = v01 + (v11 - v01) * tx
  return a + (b - a) * ty
}

/**
 * Aplica substracción DSA.
 *
 * @param {TypedArray|ArrayLike<number>} live  I_frame
 * @param {TypedArray|ArrayLike<number>} mask  I_mask
 * @param {TypedArray} out buffer reutilizable
 * @param {{
 *   cols: number,
 *   rows: number,
 *   shiftX?: number,
 *   shiftY?: number,
 *   gain?: number,
 *   offset?: number,
 *   invert?: boolean,
 *   noiseFloor?: number,
 *   min?: number,
 *   max?: number,
 * }} opts
 * @returns {TypedArray} out
 */
export function subtractFrame(live, mask, out, opts) {
  const cols = (opts && opts.cols) | 0
  const rows = (opts && opts.rows) | 0
  if (!live || !mask || !out || cols <= 0 || rows <= 0) {
    return out
  }

  const shiftX =
    opts && opts.shiftX != null && Number.isFinite(Number(opts.shiftX))
      ? Number(opts.shiftX)
      : 0
  const shiftY =
    opts && opts.shiftY != null && Number.isFinite(Number(opts.shiftY))
      ? Number(opts.shiftY)
      : 0
  const gain =
    opts && opts.gain != null && Number.isFinite(Number(opts.gain))
      ? Number(opts.gain)
      : 1
  const offset =
    opts && opts.offset != null && Number.isFinite(Number(opts.offset))
      ? Number(opts.offset)
      : 0
  const invert = !!(opts && opts.invert)
  const noiseFloor =
    opts && opts.noiseFloor != null && Number.isFinite(Number(opts.noiseFloor))
      ? Math.max(0, Number(opts.noiseFloor))
      : 0
  const minV =
    opts && opts.min != null && Number.isFinite(Number(opts.min))
      ? Number(opts.min)
      : 0
  const maxV =
    opts && opts.max != null && Number.isFinite(Number(opts.max))
      ? Number(opts.max)
      : 4095
  const span = minV + maxV

  const n = cols * rows
  const liveLen = live.length
  const maskLen = mask.length
  const outLen = out.length
  if (liveLen < n || maskLen < n || outLen < n) {
    const lim = Math.min(liveLen, maskLen, outLen)
    for (let i = 0; i < lim; i++) {
      out[i] = dsaPixel(live[i], mask[i], gain, offset, invert, noiseFloor, minV, maxV, span)
    }
    return out
  }

  const useBilinear = shiftX !== 0 || shiftY !== 0
  const intShift =
    !useBilinear ||
    (shiftX === (shiftX | 0) && shiftY === (shiftY | 0))

  if (!useBilinear) {
    for (let i = 0; i < n; i++) {
      out[i] = dsaPixel(live[i], mask[i], gain, offset, invert, noiseFloor, minV, maxV, span)
    }
    return out
  }

  if (intShift) {
    const sx = shiftX | 0
    const sy = shiftY | 0
    for (let y = 0; y < rows; y++) {
      const my = y - sy
      const rowOff = y * cols
      for (let x = 0; x < cols; x++) {
        const i = rowOff + x
        const mx = x - sx
        let maskVal = 0
        if (my >= 0 && my < rows && mx >= 0 && mx < cols) {
          maskVal = mask[my * cols + mx]
        }
        out[i] = dsaPixel(
          live[i],
          maskVal,
          gain,
          offset,
          invert,
          noiseFloor,
          minV,
          maxV,
          span
        )
      }
    }
    return out
  }

  // Sub-píxel: muestreo bilineal de la máscara
  for (let y = 0; y < rows; y++) {
    const rowOff = y * cols
    const fy = y - shiftY
    for (let x = 0; x < cols; x++) {
      const i = rowOff + x
      const maskVal = sampleMaskBilinear(mask, cols, rows, x - shiftX, fy)
      out[i] = dsaPixel(
        live[i],
        maskVal,
        gain,
        offset,
        invert,
        noiseFloor,
        minV,
        maxV,
        span
      )
    }
  }
  return out
}

/**
 * @param {number} live
 * @param {number} mask
 * @param {number} gain
 * @param {number} offset
 * @param {boolean} invert
 * @param {number} noiseFloor
 * @param {number} minV
 * @param {number} maxV
 * @param {number} span min+max
 */
function dsaPixel(live, mask, gain, offset, invert, noiseFloor, minV, maxV, span) {
  let raw = live - mask
  if (noiseFloor > 0 && raw < noiseFloor && raw > -noiseFloor) {
    raw = 0
  }
  let v = raw * gain + offset
  if (v < minV) v = minV
  else if (v > maxV) v = maxV
  if (invert) {
    v = span - v
    if (v < minV) v = minV
    else if (v > maxV) v = maxV
  }
  return v
}

/**
 * Asegura un TypedArray de salida compatible con live (reutiliza si cabe).
 * @param {TypedArray|ArrayLike<number>} live
 * @param {TypedArray|null|undefined} prev
 * @returns {TypedArray}
 */
export function ensureDsaOutBuffer(live, prev) {
  if (!live || live.length == null) {
    return prev || new Int16Array(0)
  }
  const Ctor =
    typeof live.constructor === 'function' && live.constructor !== Array
      ? live.constructor
      : Int16Array
  if (prev && prev.constructor === Ctor && prev.length === live.length) {
    return prev
  }
  return new Ctor(live.length)
}

/** Paso de pixel-shift fino (spec: 0.5 px). */
export const DSA_SHIFT_STEP = 0.5

/** Paso de pixel-shift grueso (Shift+flechas: 2.0 px). */
export const DSA_SHIFT_STEP_COARSE = 2

/** Defaults clínicos DSA. */
export const DSA_DEFAULTS = {
  invertDSA: false,
  gain: 1,
  offset: null, // null → dsaDefaultBias(image)
  noiseFloor: 8,
  pixelShift: { x: 0, y: 0 },
  engineMode: 'cpu', // 'auto' | 'gpu' | 'cpu' — CPU por defecto (estable); GPU opt-in
}
